import 'app/styles/style.scss';

import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';

import {
  isPACSConfigurationDisabled,
  selectPACSConfiguration,
} from 'app/redux/PACSConfiguration/reducer';
import { Study } from 'app/interfaces/Study';
import SyncStatus, { SyncStatusEnum } from 'app/components/SyncStatus';
import { isAcquisitionImage } from 'app/utils/isAcquisitionImage';
import PACSErrorMessage from 'app/components/PACSErrorMessage';
import preventDefault from 'app/utils/preventDefault';
import useSyncStudyFromStoreWithPACSMutation, {
  PACSSyncFailuresError,
  useSyncStudyFromStoreWithPACSMutationState,
} from 'app/queries/useSyncStudyFromStoreWithPACSMutation';

const PACS_SYNC_STATUS_TEXTS = {
  [SyncStatusEnum.SYNC]: (
    <h5>
      <FormattedMessage id="monitoring.actions.pacs.sync" />
    </h5>
  ),
  [SyncStatusEnum.ONGOING]: (
    <h5>
      <FormattedMessage id="monitoring.actions.pacs.syncing" />
    </h5>
  ),
  [SyncStatusEnum.ERROR_SYNC]: (
    <h5>
      <FormattedMessage id="monitoring.actions.pacs.sync_failed" />
    </h5>
  ),
  [SyncStatusEnum.NOT_SYNC]: (
    <div>
      <h5>
        <FormattedMessage id="monitoring.actions.pacs.not_sync" />
      </h5>
      <FormattedMessage id="monitoring.actions.pacs.not_sync.trigger" />
    </div>
  ),
};

interface PACSSyncStatusProps {
  study: Study;
}

const PACSSyncStatus: React.FC<PACSSyncStatusProps> = React.memo(({ study }) => {
  const pacsConfiguration = useSelector(selectPACSConfiguration);
  const { mutate } = useSyncStudyFromStoreWithPACSMutation();
  const { status, error } = useSyncStudyFromStoreWithPACSMutationState({ study }).at(-1) ?? {
    status: 'idle',
    error: null,
  };

  const isPACSDisabled = isPACSConfigurationDisabled(pacsConfiguration);
  if (isPACSDisabled) return null;

  const { images } = study;

  const pacsCompatibleImages = useMemo(() => images.filter(isAcquisitionImage), [images]);
  if (pacsCompatibleImages.length === 0) return null;

  const syncStatus = useMemo(() => {
    switch (status) {
      case 'pending':
        return SyncStatusEnum.ONGOING;
      case 'success':
        return SyncStatusEnum.SYNC;
      case 'error':
        return SyncStatusEnum.ERROR_SYNC;
      case 'idle': {
        const isAlreadySyncWithPACS = !pacsCompatibleImages.some(
          (image) => !image?.image_metadata?.PACS?.isSync
        );
        return isAlreadySyncWithPACS ? SyncStatusEnum.SYNC : SyncStatusEnum.NOT_SYNC;
      }
    }
  }, [status, pacsCompatibleImages]);

  const syncStatusTexts = useMemo(() => {
    let errorContent;
    if (error) {
      if (error instanceof PACSSyncFailuresError) {
        errorContent = _.map(error.syncFailures, (failure, imageId) => {
          const imageIndex = study.images.findIndex((image) => image._id === imageId);
          return (
            <PACSErrorMessage
              key={imageId}
              size="tiny"
              error={failure}
              title={
                <FormattedMessage
                  id="pacs.message.store_error.title.indexed"
                  values={{ imageIndex: imageIndex + 1 }}
                />
              }
              titleID="pacs.message.store_error.title"
            />
          );
        });
      } else {
        errorContent = error.message;
      }
    }
    return {
      ...PACS_SYNC_STATUS_TEXTS,
      [SyncStatusEnum.ERROR_SYNC]: (
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {PACS_SYNC_STATUS_TEXTS[SyncStatusEnum.ERROR_SYNC]}
          <div style={{ fontSize: '0.8rem' }}>{errorContent}</div>
        </div>
      ),
    };
  }, [error]);

  const syncOnClick = useCallback(() => {
    if (status === 'pending') return;
    mutate({ study });
  }, [study, status]);

  return (
    <div onClick={preventDefault} onDoubleClick={preventDefault}>
      <SyncStatus
        status={syncStatus}
        name="cloud upload"
        statusTexts={syncStatusTexts}
        size="large"
        onClick={syncOnClick}
      />
    </div>
  );
});

export default PACSSyncStatus;
